package com.example.mystybag.e5AiSearch.helpers

import com.example.mystybag.sharedmodels.project.responseModels.EmbeddingDeltaItem
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlin.time.TimeSource
import kotlin.math.sqrt
data class SearchHit(
    val merchantLocationId: Long,
    val score: Float
)

private val _categoryKeywordsRaw = MutableStateFlow<Map<Long, String>>(emptyMap())
val categoryKeywordsRaw: StateFlow<Map<Long, String>> = _categoryKeywordsRaw.asStateFlow()

enum class SearchIntent {
    BAKERY,        // 1. Пекарна
    SWEET,         // 2. Сладкарница
    BBQ,           // 3. Барбекю
    COFFEE,        // 4. Кафене
    SUPERMARKET,   // 5. Супермаркет
    FRUIT_VEG,     // 6. Плод-зеленчук
    MEAT,          // 7. Месарница
    FAST_FOOD,     // 8. Бърза храна
    PIZZA,         // 9. Пицария
    SUSHI,         // 10. Суши бар
    BEAUTY,        // 11. Козметично студио + 14. Маникюр и педикюр
    HAIR,          // 12. Фризьорски салон
    PET,           // 13. Зоомагазин
    TAKEAWAY,      // 15. Храна за вкъщи
    FISH,          // 16. Рибарница
    ICE_CREAM,     // 17. Сладоледжийница
    INGREDIENT,    // За суровини/съставки (риба, месо за готвене)
    HUNGRY,        // Общ intent за "гладен съм"
    HEALTHY        // Здравословна храна
}

data class IntentTuning(
    val penaltyMultiplier: Float,
    val keywordWeight: Float,
    val maxKeywordBonus: Float,
    val minCatScore: Float,
    val alpha: Float,
    val beta: Float,
    val preferredCategoryBoost: Float = 0.0f,
    val preferredCategories: Set<Long> = emptySet()
)

// 🔥 FIX #2: По-мек DEFAULT за по-добър recall при липса на intent
val DEFAULT_TUNING = IntentTuning(
    penaltyMultiplier = 0.75f,  // Было: 0.55f - твърде агресивен
    keywordWeight = 0.01f,      // Было: 0.0f - keyword-ите бяха безсмислени
    maxKeywordBonus = 0.04f,    // Было: 0.0f
    minCatScore = 0.63f,        // Было: 0.70f - CAT pass оставаше с 1 категория
    alpha = 0.75f,              // Было: 0.80f - LOC similarity беше игнориран
    beta = 0.25f                // Было: 0.20f
)

// =======================================================
// Normalization & tokenization
// =======================================================

private val BG_STOP_PHRASES = listOf(
    "хапва ми се", "яде ми се", "искам да ям", "много ми се яде",
    "някакво", "някакъв", "дайте", "моля"
)

private val BG_STOP_WORDS = setOf(
    "ми","се","да","на","за","с","в","от","до","и","или","ама","че",
    "нещо","някакво","някакъв","някаква","искам","ям","яде"
)

private val EXTRA_STOP_WORDS = setOf(
    "and","or","the","a","an","to","go","bar","ready","meals","grab"
)

private val INTENT_TRIGGERS: Map<SearchIntent, Set<String>> = mapOf(

    SearchIntent.BAKERY to setOf(
        "пекарна", "хляб", "баница", "кифла", "кроасан", "багета", 
        "питка", "погача", "топъл хляб", "прясно изпечен"
    ),

    SearchIntent.SWEET to setOf(
        "сладко", "десерт", "торта", "еклер", "профитерол", "браун",
        "мъфин", "палачин", "вафл", "сладкарница", "сладкиш"
    ),

    SearchIntent.BBQ to setOf(
        "барбекю", "скара", "шашлик", "ребърца", "пържол", "грил",
        "печено месо", "роштилj"
    ),

    SearchIntent.COFFEE to setOf(
        "кафе", "еспресо", "капучино", "лате", "кафене"
    ),

    SearchIntent.SUPERMARKET to setOf(
        "супермаркет", "магазин", "хранителни", "продукти", "пазар"
    ),

    SearchIntent.FRUIT_VEG to setOf(
        "плодове", "зеленчуци", "пресни", "сезонни", "био", "пресен"
    ),

    SearchIntent.MEAT to setOf(
        "месо", "месарница", "пилешко", "телешко", "свинско", "кайма",
        "карначе", "салам", "колбас"
    ),

    SearchIntent.FAST_FOOD to setOf(
        "бърза храна", "сандвич", "дюнер", "кюфте", "кебап", "бургер"
    ),

    SearchIntent.PIZZA to setOf(
        "пица", "пицария", "лазаня", "спагети", "паста"
    ),

    SearchIntent.SUSHI to setOf(
        "суши", "сашими", "маки", "нигири", "японска"
    ),

    SearchIntent.BEAUTY to setOf(
        "козметика", "маникюр", "педикюр", "нокти", "красота",
        "козметично", "студио"
    ),

    SearchIntent.HAIR to setOf(
        "фризьор", "фризьорск", "подстриг", "прическ", "дамск",
        "боядис", "коса", "оформян"
    ),

    SearchIntent.PET to setOf(
        "зоомагазин", "храна за кучета", "храна за котки", "аквариум",
        "домашни любимци", "зоо"
    ),

    SearchIntent.TAKEAWAY to setOf(
        "супа", "мусака", "сарми", "пиперк", "шкембе", "яхния", "манджа",
        "храна за вкъщи", "готова храна"
    ),

    SearchIntent.FISH to setOf(
        "рибарница", "пресна риба", "морска риба", "речна риба",
        "филе риба", "морски дарове"
    ),

    SearchIntent.ICE_CREAM to setOf(
        "сладолед", "сладоледжийница", "топче", "вафла", "замразено",
        "студено", "джелато"
    ),

    SearchIntent.INGREDIENT to setOf(
        "риба", "сьомга", "ципура", "лаврак", "скумрия", "морска",
        "прясна риба", "пресна риба", "купи риба", "купи месо",
        "суровини", "съставки"
    ),

    SearchIntent.HUNGRY to setOf(
        "гладен", "гладна", "гладни",
        "ям", "яде", "ядене",
        "храна", "вкусно", "нещо"
    ),

    SearchIntent.HEALTHY to setOf(
        "здравослов", "био", "органик",
        "веган", "вегетариан",
        "без захар", "без глутен"
    )
)

fun normalizeQueryForSearch(q: String): String {
    var s = q.trim().lowercase()
    for (p in BG_STOP_PHRASES) s = s.replace(p, " ")
    val tokens = splitWhitespaceTokens(sanitizeLettersDigitsWhitespace(s))
        .filter { it !in BG_STOP_WORDS }
    return tokens.joinToString(" ")
}

private fun tokenize(q: String): List<String> {
    val tokens = splitWhitespaceTokens(sanitizeLettersDigitsWhitespace(q.lowercase()))
    return tokens.filter { it !in EXTRA_STOP_WORDS }
}

// 🔥 FIX #1: Situational intent enrichment за по-добро UX
private val SITUATIONAL_HUNGRY = setOf(
    "какво", "поръч", "обяд", "вечеря", "сега", "набързо", "взем",
    "закуска", "храна", "ям", "яде"
)

fun detectIntent(cleaned: String): Set<SearchIntent> {
    if (cleaned.isBlank()) return emptySet()
    val tokens = splitWhitespaceTokens(cleaned)
    val intents = INTENT_TRIGGERS
        .filter { (_, triggers) ->
            tokens.any { t -> triggers.any { trig -> t.startsWith(trig) } }
        }
        .keys
        .toMutableSet()
    
    // 🔥 Добавяме HUNGRY при situational keywords, ако няма друг intent
    if (intents.isEmpty() && tokens.any { t -> SITUATIONAL_HUNGRY.any { s -> t.startsWith(s) } }) {
        intents += SearchIntent.HUNGRY
    }
    
    // 🔥 INGREDIENT има precedence над HUNGRY (купувам vs ям сега)
    if (SearchIntent.INGREDIENT in intents && SearchIntent.HUNGRY in intents) {
        intents.remove(SearchIntent.HUNGRY)
    }
    
    return intents
}

fun setCategoryKeywords(keywords: Map<Long, String>) {
    _categoryKeywordsRaw.value = keywords
}

private fun getCategoryKeywordStems(catId: Long): Set<String> {
    val raw = categoryKeywordsRaw.value[catId] ?: return emptySet()
    // Kotlin/Native's regex engine doesn't support \p{L}/\p{N}; avoid regex for iOS.
    return splitAlphaNumTokensLower(raw, minLen = 3).toSet()
}

// =======================================================
// Regex-free text helpers (Kotlin/Native compatible)
// =======================================================

private fun sanitizeLettersDigitsWhitespace(input: String): String = buildString(input.length) {
    for (ch in input) {
        when {
            ch.isLetterOrDigit() || ch.isWhitespace() -> append(ch)
            else -> append(' ')
        }
    }
}

private fun splitWhitespaceTokens(input: String): List<String> {
    val out = ArrayList<String>()
    var i = 0
    val n = input.length
    while (i < n) {
        while (i < n && input[i].isWhitespace()) i++
        val start = i
        while (i < n && !input[i].isWhitespace()) i++
        if (start < i) out.add(input.substring(start, i))
    }
    return out
}

private fun splitAlphaNumTokensLower(input: String, minLen: Int): List<String> {
    val s = input.lowercase()
    val out = ArrayList<String>()
    val sb = StringBuilder()

    fun flush() {
        if (sb.length >= minLen) out.add(sb.toString())
        sb.setLength(0)
    }

    for (ch in s) {
        if (ch.isLetterOrDigit()) sb.append(ch) else flush()
    }
    flush()
    return out
}

private fun stemMatchesToken(token: String, stem: String, minPrefix: Int = 3): Boolean {
    if (token.isEmpty() || stem.isEmpty()) return false
    if (token.startsWith(stem) || stem.startsWith(token)) return true
    return commonPrefixLen(token, stem) >= minPrefix
}

private fun commonPrefixLen(a: String, b: String): Int {
    val n = minOf(a.length, b.length)
    var i = 0
    while (i < n && a[i] == b[i]) i++
    return i
}

private fun keywordBoost(
    catId: Long?,
    tokens: List<String>,
    tuning: IntentTuning
): Pair<Int, Float> {

    if (catId == null) return 0 to 0f
    val stems = getCategoryKeywordStems(catId)
    if (stems.isEmpty()) return 0 to 0f

    var hits = 0
    for (t in tokens) {
        if (stems.any { s -> stemMatchesToken(t, s) }) hits++
    }

    val bonus = (hits * tuning.keywordWeight)
        .coerceAtMost(tuning.maxKeywordBonus)

    return hits to bonus
}

suspend fun searchLocalTwoStage(
    embedder: Embedder,
    queryText: String,
    embeddings: List<EmbeddingDeltaItem>,
    categoryIdByLocationId: Map<Long, Long>,
    topKCat: Int = 3,
    topN: Int = 50
): List<SearchHit> {

    val tAll = TimeSource.Monotonic.markNow()
    val cleaned = normalizeQueryForSearch(queryText)
    val tokens = tokenize(cleaned.ifBlank { queryText })
    val intents = detectIntent(cleaned)
    val tuning = SearchRankingRuntime.resolve(intents)
    
    // 🔥 FIX #3, #4, #5: Adaptive tuning при липса на intent
    val hasIntent = intents.isNotEmpty()
    val effectiveKeywordWeight = if (!hasIntent) 0.01f else tuning.keywordWeight
    val effectiveMinCatScore = if (!hasIntent) tuning.minCatScore - 0.05f else tuning.minCatScore
    val effectiveAlpha = if (!hasIntent) 0.70f else tuning.alpha
    val effectiveBeta = 1f - effectiveAlpha

    val catCount = embeddings.count { it.docType == "CAT" }
    val locCount = embeddings.count { it.docType == "LOC" }
    val unknownCount = embeddings.size - catCount - locCount

    logSearch(
        "start q='${queryText.trunc(80)}' cleaned='${cleaned.trunc(80)}' " +
            "tokens=${tokens.take(8)}(n=${tokens.size}) intents=$intents hasIntent=$hasIntent " +
            "tuning={alpha=$effectiveAlpha→${tuning.alpha},beta=$effectiveBeta→${tuning.beta}," +
            "minCat=$effectiveMinCatScore→${tuning.minCatScore}," +
            "penalty=${tuning.penaltyMultiplier},kwW=$effectiveKeywordWeight→${tuning.keywordWeight}," +
            "kwMax=${tuning.maxKeywordBonus}," +
            "prefBoost=${tuning.preferredCategoryBoost},prefCats=${tuning.preferredCategories}} " +
            "embeddings={total=${embeddings.size},CAT=$catCount,LOC=$locCount,other=$unknownCount}"
    )

    val tEmbed = TimeSource.Monotonic.markNow()
    val queryVec = embedder.embedQuery(cleaned.ifBlank { queryText })
    l2NormalizeInPlace(queryVec)
    logSearch(
        "embedQuery done in ${tEmbed.elapsedNow()} vecDim=${queryVec.size} l2~${l2(queryVec)} first8=${queryVec.take(8)}"
    )

    val tmp = FloatArray(queryVec.size)

    // ---------- CAT PASS ----------
    val catScores = HashMap<Long, Float>()
    val bestAnyCats = TopK<CatDbg>(k = 8) { it.finalScore }
    var maxCatScoreAny = Float.NEGATIVE_INFINITY

    for (e in embeddings) {
        if (e.docType != "CAT") continue

        val locId = e.merchantLocationId
        val catId = categoryIdByLocationId[locId]

        dequantizeToFloat(e, tmp)
        l2NormalizeInPlace(tmp)

        val rawDot = dot(queryVec, tmp)
        var score = rawDot

        // Check if category is allowed based on preferredCategories from tuning
        // If preferredCategories is empty, all categories are allowed
        // If preferredCategories is not empty, only categories in the list are allowed
        val allowed = tuning.preferredCategories.isEmpty() || 
                     (catId != null && catId in tuning.preferredCategories)

        val afterPenalty = if (!allowed) score * tuning.penaltyMultiplier else score
        score = afterPenalty

        // 🔥 FIX #3: Използваме effectiveKeywordWeight вместо tuning.keywordWeight
        val effectiveTuning = tuning.copy(keywordWeight = effectiveKeywordWeight)
        val (kwHits, kwBonus) = keywordBoost(catId, tokens, effectiveTuning)
        score += kwBonus

        val prefBoost = if (catId != null && catId in tuning.preferredCategories) tuning.preferredCategoryBoost else 0f
        score += prefBoost

        maxCatScoreAny = maxOf(maxCatScoreAny, score)
        bestAnyCats.add(
            CatDbg(
                locId = locId,
                catId = catId,
                rawDot = rawDot,
                allowed = allowed,
                afterPenalty = afterPenalty,
                kwHits = kwHits,
                kwBonus = kwBonus,
                prefBoost = prefBoost,
                finalScore = score
            )
        )

        // 🔥 FIX #4: Използваме effectiveMinCatScore за adaptive filtering
        if (score >= effectiveMinCatScore) {
            catScores[locId] = score
        }
    }

    if (catScores.isEmpty()) {
        logSearch(
            "CAT pass -> 0 candidates over minCatScore=$effectiveMinCatScore (orig=${tuning.minCatScore}). " +
                "bestAny(maxScore=$maxCatScoreAny)=${bestAnyCats.itemsSortedDesc()}"
        )
        logSearch("end (empty) totalTime=${tAll.elapsedNow()}")
        return emptyList()
    }

    val topCats = catScores.entries
        .sortedByDescending { it.value }
        .take(topKCat)
        .associate { it.key to it.value }

    logSearch("CAT pass -> kept=${catScores.size} topCats=${topCats.entries.sortedByDescending { it.value }.map { "${it.key}:${it.value}" }}")

    // ---------- LOC PASS ----------
    val results = ArrayList<SearchHit>()
    val bestLocHits = TopK<SearchHit>(k = 10) { it.score }

    for (e in embeddings) {
        if (e.docType != "LOC") continue

        val catScore = topCats[e.merchantLocationId] ?: continue

        dequantizeToFloat(e, tmp)
        l2NormalizeInPlace(tmp)

        val locScore = dot(queryVec, tmp)
        // 🔥 Използваме effectiveAlpha/Beta за по-добро balance при липса на intent
        val finalScore = effectiveAlpha * catScore + effectiveBeta * locScore

        val hit = SearchHit(e.merchantLocationId, finalScore)
        results.add(hit)
        bestLocHits.add(hit)
    }

    val sortedResults = results
        .sortedByDescending { it.score }
        .take(topN)

    logSearch("LOC pass -> hits=${results.size} top10=${bestLocHits.itemsSortedDesc()}")

    // 🔥 Dynamic penalty adjustment: ако имаме малко резултати, релаксираме филтрирането
    val useDynamicFilter = sortedResults.size <= 3
    
    // Филтриране по бизнес категории с прагове за score
    val filtered = if (useDynamicFilter) {
        logSearch("dynamicFilter=ON (few results, softening category filter)")
        filterResultsByCategoryScoreSoft(sortedResults, categoryIdByLocationId)
    } else {
        filterResultsByCategoryScore(sortedResults, categoryIdByLocationId)
    }
    logSearch(
        "filterByCategoryScore -> in=${sortedResults.size} out=${filtered.size} " +
            "catsTop=${filtered.take(10).map { hit -> categoryIdByLocationId[hit.merchantLocationId] }}"
    )
    logSearch("end totalTime=${tAll.elapsedNow()}")
    return filtered
}

// =======================================================
// Debug logging helpers
// =======================================================

private const val E5_SEARCH_TAG = "E5SRCH"

private fun logSearch(msg: String) {
    E5Platform.log(E5_SEARCH_TAG, msg)
}

private fun String.trunc(max: Int): String =
    if (length <= max) this else substring(0, max) + "…"

private fun l2(v: FloatArray): Float {
    var sum = 0f
    for (x in v) sum += x * x
    return sqrt(sum)
}

private data class CatDbg(
    val locId: Long,
    val catId: Long?,
    val rawDot: Float,
    val allowed: Boolean,
    val afterPenalty: Float,
    val kwHits: Int,
    val kwBonus: Float,
    val prefBoost: Float,
    val finalScore: Float
) {
    override fun toString(): String =
        "loc=$locId cat=$catId raw=${fmt(rawDot)} allowed=$allowed pen=${fmt(afterPenalty)} kwHits=$kwHits kw=${fmt(kwBonus)} pref=${fmt(prefBoost)} final=${fmt(finalScore)}"
}

private fun fmt(x: Float): String = ((x * 10000).toInt() / 10000.0f).toString()

private class TopK<T>(
    private val k: Int,
    private val scoreOf: (T) -> Float
) {
    private val items = ArrayList<T>(k + 1)

    fun add(v: T) {
        items.add(v)
        items.sortByDescending(scoreOf)
        if (items.size > k) items.removeAt(items.lastIndex)
    }

    fun itemsSortedDesc(): List<T> = items.toList()
}

// =======================================================
// Category score filtering
// =======================================================

/**
 * ULTRA-SOFT filtering - използва се когато имаме много малко резултати.
 * Пуска всички категории с score >= (topScore - 0.10)
 */
private fun filterResultsByCategoryScoreSoft(
    results: List<SearchHit>,
    categoryIdByLocationId: Map<Long, Long>
): List<SearchHit> {
    if (results.isEmpty()) return results
    
    val topScore = results.firstOrNull()?.score ?: 0f
    val ultraSoftThreshold = topScore - 0.10f
    
    return results.filter { it.score >= ultraSoftThreshold }
}

/**
 * Филтрира резултатите по бизнес категории с SOFT прагове за score:
 * - Първата категория: всички резултати
 * - Останалите категории: score >= (topScore - 0.05)
 * 
 * Това предотвратява изключването на валидни вторични категории.
 */
private fun filterResultsByCategoryScore(
    results: List<SearchHit>,
    categoryIdByLocationId: Map<Long, Long>
): List<SearchHit> {
    if (results.isEmpty()) return results

    val topScore = results.firstOrNull()?.score ?: 0f
    
    // Групиране по бизнес категория
    val resultsByCategory = results.groupBy { hit ->
        categoryIdByLocationId[hit.merchantLocationId]
    }

    // Намиране на най-добрия score за всяка категория
    val categoryBestScores = resultsByCategory.mapValues { (_, hits) ->
        hits.maxOfOrNull { it.score } ?: 0f
    }

    // Сортиране на категориите по най-добър score (най-добрите първи)
    val sortedCategories = categoryBestScores.entries
        .sortedByDescending { it.value }
        .map { it.key }

    val filteredResults = mutableListOf<SearchHit>()
    val softThreshold = topScore - 0.05f  // 🔥 SOFT threshold

    sortedCategories.forEachIndexed { index, categoryId ->
        val categoryResults = resultsByCategory[categoryId] ?: return@forEachIndexed

        when (index) {
            0 -> {
                // Първата категория: всички резултати
                filteredResults.addAll(categoryResults)
            }
            else -> {
                // Останалите категории: само ако са близо до top score
                filteredResults.addAll(categoryResults.filter { it.score >= softThreshold })
            }
        }
    }

    // Сортиране по score (най-добрите първи)
    return filteredResults.sortedByDescending { it.score }
}

// =======================================================
// Math helpers
// =======================================================

private fun dequantizeToFloat(e: EmbeddingDeltaItem, out: FloatArray) {
    val zp = e.qZeroPoint
    val scale = e.qScale
    for (i in out.indices) {
        out[i] = (e.vecQint8[i].toInt() - zp) * scale
    }
}

private fun dot(a: FloatArray, b: FloatArray): Float {
    var s = 0f
    for (i in a.indices) s += a[i] * b[i]
    return s
}

private fun l2NormalizeInPlace(v: FloatArray) {
    var sum = 0f
    for (x in v) sum += x * x
    val n = sqrt(sum)
    if (n == 0f) return
    for (i in v.indices) v[i] /= n
}
